// Display helpers for camera capability data.
//
// The database has two tiers. A `full` camera has a confirmed video-mode
// table, profile list and image-style controls. An `exposure-only` camera
// has aperture, base ISO and ND class but no video specs — enough for the
// shutter/ISO/ND math, not enough to name a resolution.
//
// Without these helpers the pages print `frameRates?.join(", ")` on an
// empty array, which renders as a bare "fps" with nothing in front of it.
// Every function here returns a sentence a person can read, including
// when the answer is "we don't know."

const UNKNOWN = "Not confirmed in our database";

export function isFullSpec(camera) {
  return (camera.capabilityLevel || "full") === "full";
}

export function frameRateSummary(camera) {
  const rates = camera.capabilities.frameRates || [];
  if (!rates.length) return UNKNOWN;
  return `${rates.join(", ")}fps`;
}

export function resolutionSummary(camera) {
  const res = camera.capabilities.resolutions || [];
  if (!res.length) return UNKNOWN;
  return [...new Set(res)].join(", ");
}

export function profileSummary(camera) {
  const profiles = camera.capabilities.profiles || [];
  if (!profiles.length) return UNKNOWN;
  return profiles.join(", ");
}

export function apertureSummary(camera) {
  const ap = camera.capabilities.aperture;
  if (!ap || !ap.min) return "Depends on the lens fitted";
  if (ap.variable && ap.max > ap.min) return `f/${ap.min}–f/${ap.max} (adjustable)`;
  return `f/${ap.min} (fixed)`;
}

export function ndSummary(camera) {
  const caps = camera.capabilities;
  if (caps.ndSupport === false) return "Not natively supported";
  if (caps.ndSupport === null || caps.ndSupport === undefined) return UNKNOWN;
  if (caps.ndBuiltIn) return "Built in";
  const strengths = caps.ndStrengths || [];
  return strengths.length ? `Supported: ${strengths.join(", ")}` : "Supported";
}

export function stabilizationSummary(camera) {
  const caps = camera.capabilities;
  if (caps.stabilization === false) return "None";
  if (caps.stabilization === null || caps.stabilization === undefined) return UNKNOWN;
  const levels = caps.stabilizationLevels || [];
  return levels.length ? levels.join(", ") : "Supported";
}

/**
 * A short, honest line about how much we actually know about this camera,
 * shown on hub pages so nobody has to infer it from a thin spec list.
 */
export function coverageNote(camera) {
  if (isFullSpec(camera)) {
    return camera.dataConfidence === "provisional"
      ? "This camera is recent enough that its specs haven't been cross-checked against a manufacturer sheet yet. Verify against your own menu before trusting the numbers."
      : null;
  }
  return "We hold this camera's exposure characteristics (aperture, base ISO, ND options) but not its full resolution and frame-rate table, so recipes for it specify exposure precisely and leave resolution to you.";
}
